import React, { useState } from "react";
import { Mail, MapPin, Clock, Instagram, Facebook, Linkedin, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "@/components/site/SectionHeader";
import { submitContact, submitPartnership } from "@/lib/api";
import { SOCIAL } from "@/lib/social";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", organisation: "", enquirer_type: "" });
  const [loading, setLoading] = useState(false);
  const [collab, setCollab] = useState({ organisation: "", contact_name: "", email: "", role: "", partnership_type: "", message: "" });
  const [collabLoading, setCollabLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onCollabChange = (k) => (e) => setCollab((c) => ({ ...c, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const message = form.enquirer_type
        ? `Enquiring on behalf of: ${form.enquirer_type}\n\n${form.message}`
        : form.message;
      await submitContact({
        name: form.name,
        email: form.email,
        subject: form.subject,
        organisation: form.organisation,
        message,
      });
      toast.success("Kia ora — message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "", organisation: "", enquirer_type: "" });
    } catch {
      toast.error("Couldn't send your message. Please try again.");
    } finally { setLoading(false); }
  };

  const onCollabSubmit = async (e) => {
    e.preventDefault();
    setCollabLoading(true);
    try {
      await submitPartnership(collab);
      toast.success("Enquiry sent — ngā mihi.");
      setCollab({ organisation: "", contact_name: "", email: "", role: "", partnership_type: "", message: "" });
    } catch {
      toast.error("Couldn't send enquiry. Please try again.");
    } finally { setCollabLoading(false); }
  };

  return (
    <>
      <section className="container-x pt-20 pb-12 sm:pt-28">
        <div className="max-w-4xl">
          <span className="label-eyebrow">— Get in touch</span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-heading font-semibold tracking-tighter leading-[1.02] text-balance">
            Kia ora — let's start the kōrero.
          </h1>
          <p className="mt-8 text-lg text-ink/65 max-w-2xl leading-relaxed">
            Whether you're a school, community group, funder, journalist or a young person looking for support — we'd
            love to hear from you.
          </p>
        </div>
      </section>

      <section className="section-y !pt-12">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <div className="card-surface !p-7">
              <Mail className="w-5 h-5 text-crimson mb-4" />
              <span className="label-eyebrow">— Email</span>
              <a href="mailto:alex.amboy@aoturoaacademy.org.nz" data-testid="contact-email-link" className="mt-3 block text-lg font-heading font-medium text-ink break-all hover:text-crimson transition-colors">
                alex.amboy@aoturoaacademy.org.nz
              </a>
            </div>
            <div className="card-surface !p-7">
              <MapPin className="w-5 h-5 text-crimson mb-4" />
              <span className="label-eyebrow">— Location</span>
              <p className="mt-3 text-ink font-heading font-medium">Tāmaki Makaurau, Auckland</p>
              <p className="text-sm text-ink/55 mt-1">Aotearoa New Zealand</p>
            </div>
            <div className="card-surface !p-7">
              <Clock className="w-5 h-5 text-crimson mb-4" />
              <span className="label-eyebrow">— Response time</span>
              <p className="mt-3 text-ink font-heading font-medium">Within 2–3 business days</p>
              <p className="text-sm text-ink/55 mt-1">Every enquiry is read personally</p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              {[
                { Icon: Instagram, label: "Instagram", href: SOCIAL.instagram },
                { Icon: Facebook, label: "Facebook", href: SOCIAL.facebook },
                { Icon: Linkedin, label: "LinkedIn", href: SOCIAL.linkedin },
              ].map(({Icon, label, href}) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" data-testid={`contact-social-${label.toLowerCase()}`} aria-label={label} className="w-11 h-11 rounded-full bg-white border border-stone flex items-center justify-center hover:bg-ink hover:text-bone hover:border-ink transition-all">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-7 bg-white rounded-3xl p-8 sm:p-10 border border-stone h-fit" data-testid="contact-form">
            <h2 className="text-2xl font-heading font-semibold text-ink">Send us a message</h2>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-ink/80 mb-2 block">Name</label>
                <input required className="input-field" value={form.name} onChange={onChange("name")} data-testid="contact-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-ink/80 mb-2 block">Email</label>
                <input required type="email" className="input-field" value={form.email} onChange={onChange("email")} data-testid="contact-email" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-ink/80 mb-2 block">Organisation (optional)</label>
                <input className="input-field" value={form.organisation} onChange={onChange("organisation")} data-testid="contact-organisation" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-ink/80 mb-2 block">I am enquiring on behalf of</label>
                <select className="input-field" value={form.enquirer_type} onChange={onChange("enquirer_type")} data-testid="contact-enquirer-type">
                  <option value="">Select (optional)...</option>
                  <option>A school</option>
                  <option>An organisation</option>
                  <option>A community service</option>
                  <option>A professional team</option>
                  <option>Another group</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-ink/80 mb-2 block">Subject</label>
                <input required className="input-field" value={form.subject} onChange={onChange("subject")} data-testid="contact-subject" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-ink/80 mb-2 block">Message</label>
                <textarea required rows={6} className="input-field resize-none" value={form.message} onChange={onChange("message")} data-testid="contact-message" />
              </div>
            </div>
            <button type="submit" disabled={loading} className="mt-7 btn-primary" data-testid="contact-submit">
              {loading ? "Sending..." : <>Send message <ArrowUpRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </section>

      {/* COLLABORATION ENQUIRY */}
      <section className="bg-sand">
        <div className="container-x py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionHeader eyebrow="Collaboration enquiry" title="Working together for rangatahi." description="If you'd like to explore community collaboration — schools, kura, iwi, NGOs, funders — this is the place to start." />
            </div>
            <form onSubmit={onCollabSubmit} className="lg:col-span-7 bg-white rounded-3xl p-8 sm:p-10 border border-stone" data-testid="collaboration-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Organisation</label>
                  <input required className="input-field" value={collab.organisation} onChange={onCollabChange("organisation")} data-testid="collab-organisation" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Contact name</label>
                  <input required className="input-field" value={collab.contact_name} onChange={onCollabChange("contact_name")} data-testid="collab-contact-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Email</label>
                  <input required type="email" className="input-field" value={collab.email} onChange={onCollabChange("email")} data-testid="collab-email" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-ink/80 mb-2 block">How would you like to collaborate?</label>
                  <textarea required rows={5} className="input-field resize-none" value={collab.message} onChange={onCollabChange("message")} data-testid="collab-message" />
                </div>
              </div>
              <button type="submit" disabled={collabLoading} className="mt-7 btn-crimson" data-testid="collab-submit">
                {collabLoading ? "Sending..." : <>Submit enquiry <ArrowUpRight className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="container-x py-24">
        <SectionHeader eyebrow="Find us" title="Tāmaki Makaurau, Auckland." />
        <div className="mt-10 rounded-3xl overflow-hidden border border-stone aspect-[16/8]">
          <iframe
            title="Auckland location"
            data-testid="auckland-map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12759.367!2d174.7633!3d-36.8485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6d0d47fb5a9ce6fb%3A0xf00ef62249f3deb0!2sAuckland!5e0!3m2!1sen!2snz!4v1700000000000"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </>
  );
}
